// The drawer's two tabs, built from what the sidebar entry already has. Profile: every
// step and linear tag of the feature on one shared cm axis. Lanes: slots as rows
// (slot 1 on top) along the way, direction arrows, divider lines between rows, the
// restriction class as fill.
import { ACCESS_CLASS_COLORS } from "../layers/style/util/restrictionClass.mjs";
import { axisEndCm } from "./positions.mjs";
import { el, renderProfile, renderStepBlock } from "./renderValues.mjs";

const DIR_ARROW = {
  forward: "→",
  backward: "←",
  both_ways: "↔",
  reversible: "⇆",
};
const DIVIDERS = ["none", "solid", "double_solid", "long_dash", "short_dash"];
const percent = (cm, axis) => `${(cm / axis) * 100}%`;

function axisRow(doc, axis) {
  const ticks = el(doc, "div", "fe-daxis");
  ticks.append(
    el(doc, "span", undefined, "0"),
    el(doc, "span", undefined, `${axis} cm`),
  );
  return ticks;
}

function plotWithCursor(doc, axis, className) {
  const plot = el(doc, "div", className);
  plot.dataset.axis = String(axis);
  const cursor = el(doc, "i", "fe-cursor");
  cursor.style.display = "none";
  plot.append(cursor);
  return plot;
}

/**
 * @param {Document} doc
 * @param {import("./featureModel.mjs").FeatureModel} model
 */
export function renderDrawerProfile(doc, model) {
  const tags = model.groups
    .flatMap((g) => g.tags)
    .filter((t) => t.sections && (t.lr === "step" || t.lr === "linear"));
  if (tags.length === 0)
    return el(doc, "div", "fe-muted", "no step or linear tags");
  const axis = axisEndCm(
    model.lengthCm,
    tags.flatMap((t) => t.sections),
  );
  const node = el(doc, "div", "fe-dprofile");
  const grid = el(doc, "div", "fe-dgrid");
  tags.forEach((tag, i) => {
    // Explicit rows: the cursor overlay spans column 2 and would push auto-placed
    // tracks out of it.
    const label = el(doc, "div", "fe-dlabel", tag.rawKey);
    label.style.gridRow = String(i + 1);
    grid.append(label);
    const track = el(doc, "div", "fe-dtrack");
    track.style.gridRow = String(i + 1);
    track.append(
      tag.lr === "linear"
        ? renderProfile(doc, tag, axis, { points: false })
        : renderStepBlock(doc, tag, axis, { list: false }),
    );
    grid.append(track);
  });
  // One cursor over every track: spans the track column across all rows.
  const plot = plotWithCursor(doc, axis, "fe-dplot");
  plot.style.gridColumn = "2";
  plot.style.gridRow = `1 / ${tags.length + 1}`;
  grid.append(plot);
  node.append(grid, axisRow(doc, axis));
  return node;
}

/**
 * @param {Document} doc
 * @param {{stretches: object[]}|null} laneView from buildLaneView
 * @param {number|null} lengthCm
 */
export function renderDrawerLanes(doc, laneView, lengthCm) {
  if (!laneView || laneView.stretches.length === 0)
    return el(doc, "div", "fe-muted", "no lane slot tags");
  const axis = axisEndCm(
    lengthCm,
    laneView.stretches.map((s) => ({ startCm: s.startCm, endCm: s.endCm })),
  );
  const rows = Math.max(...laneView.stretches.map((s) => s.slotCount));
  const node = el(doc, "div", "fe-dlanes");
  const grid = el(doc, "div", "fe-dlgrid");
  const slots = el(doc, "div", "fe-dslots");
  for (let i = 0; i < rows; i++)
    slots.append(el(doc, "div", "fe-dslot", i + 1));
  const plot = plotWithCursor(doc, axis, "fe-dplot fe-dlplot");
  plot.style.height = `${rows * 24}px`;
  laneView.stretches.forEach((s) => {
    for (let i = 0; i < s.slotCount; i++) {
      const dir = s.direction ? s.direction[i] : "";
      const cell = el(doc, "div", "fe-dcell", DIR_ARROW[dir] ?? "");
      cell.style.left = percent(s.startCm, axis);
      cell.style.width = percent(s.endCm - s.startCm, axis);
      cell.style.top = `${i * 24}px`;
      cell.dataset.start = String(s.startCm);
      cell.dataset.end = String(s.endCm);
      const cls = s.classes ? s.classes[i] : null;
      if (cls) {
        cell.style.background = ACCESS_CLASS_COLORS[cls];
        cell.classList.add("fe-dfilled");
      }
      if (i > 0) {
        const div = s.dividers ? s.dividers[i - 1] : "";
        cell.classList.add(
          !div
            ? "fe-hdiv-untagged"
            : DIVIDERS.includes(div)
              ? `fe-hdiv-${div}`
              : "fe-hdiv-other",
        );
      }
      const tip = [`${s.startCm}–${s.endCm}`, `slot ${i + 1}`];
      if (dir) tip.push(`lanes:direction ${dir}`);
      if (cls) tip.push(cls);
      cell.dataset.tip = tip.join(" · ");
      plot.append(cell);
    }
  });
  grid.append(slots, plot);
  node.append(grid, axisRow(doc, axis));
  return node;
}
